import { useState, useCallback, useEffect, useRef } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// CONFIGURATION — Replace these with your actual credentials
// ─────────────────────────────────────────────────────────────────────────────
const CONFIG = {
  // Google Apps Script Web App URL (from your deployed Apps Script)
  GOOGLE_SCRIPT_URL:
    "https://script.google.com/macros/s/AKfycbx2PXQh4NmJ_oL6w4LCVXaRWhmnNn92vgCGDEgqjwd26Aqd8bW-Arfq29QH97kmXxHF/exec",

  // EmailJS
  EMAILJS_SERVICE_ID: "service_e7p0qyg",
  EMAILJS_TEMPLATE_ID: "template_w693tw3",
  EMAILJS_PUBLIC_KEY: "PgQO9dJupTDn9Lj5z",

  // App URL (must match exactly for verification links)
  APP_URL: "https://assess.qubitguardian.com",
};

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS & HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  bg: "#080808",
  surface: "#111",
  surfaceHigh: "#181818",
  border: "#1e1e1e",
  orange: "#F5A623",
  orangeDim: "#a36a10",
  red: "#E53935",
  green: "#43A047",
  yellow: "#FFC107",
  white: "#EFEFEF",
  dim: "#FFF",
};

function fmt(n) {
  if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

function generateToken() {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

function simulateQuantum() {
  const hits = Math.floor(Math.random() * 200) + 780;
  return {
    factor: hits / 1000,
    result: `{'011': ${Math.floor(hits * 0.51)}, '111': ${hits - Math.floor(hits * 0.51)}}`,
  };
}

function calcHndl(factor, years) {
  return Math.min(0.97, 0.15 * (1 + factor * years * 0.4) * years);
}

const PEER_BANKS = [
  { name: "JPMorgan Chase", pct: 78, status: "Advanced" },
  { name: "Goldman Sachs", pct: 71, status: "Advanced" },
  { name: "Bank of America", pct: 54, status: "In Progress" },
  { name: "Wells Fargo", pct: 48, status: "In Progress" },
  { name: "Citigroup", pct: 45, status: "In Progress" },
  { name: "Morgan Stanley", pct: 31, status: "Planning" },
  { name: "U.S. Bancorp", pct: 22, status: "Planning" },
  { name: "Industry Average", pct: 38, status: "Planning", benchmark: true },
];

const TIMELINE = [
  {
    day: 1,
    cost: 150_000,
    event: "Breach discovered. Incident response activated.",
    consequence: "SEC 4-day disclosure clock starts. Outside counsel engaged.",
  },
  {
    day: 4,
    cost: 800_000,
    event: "SEC 8-K material cybersecurity disclosure deadline.",
    consequence:
      "Failure to disclose = additional SEC violation. Stock impact begins.",
  },
  {
    day: 7,
    cost: 1_200_000,
    event: "Forensic scope of RSA key compromise determined.",
    consequence: "Customer notification obligation triggered.",
  },
  {
    day: 14,
    cost: 2_100_000,
    event: "OCC, FDIC, Federal Reserve formally notified.",
    consequence: "Congressional inquiries begin. Media coverage intensifies.",
  },
  {
    day: 30,
    cost: 4_500_000,
    event: "Class action lawsuit filed.",
    consequence:
      "D&O insurance claims activated. Board members named individually.",
  },
  {
    day: 60,
    cost: 7_800_000,
    event: "GLBA Safeguards Rule violation confirmed by OCC.",
    consequence: "Daily fines of $100K begin. Customer attrition accelerates.",
  },
  {
    day: 90,
    cost: 12_500_000,
    event: "FedRAMP ATO suspended for cloud services.",
    consequence: "Federal contracts paused. Revenue loss compounds daily.",
  },
  {
    day: 168,
    cost: 18_900_000,
    event: "Average FS breach containment date (IBM 2025).",
    consequence: "Full scope of HNDL data now decryptable by adversary.",
  },
  {
    day: 279,
    cost: 28_400_000,
    event: "Final breach containment achieved.",
    consequence:
      "Regulatory investigation concluded. Settlement negotiations begin.",
  },
  {
    day: 365,
    cost: 42_000_000,
    event: "One year post-breach.",
    consequence:
      "Class action settled. Fines final. Reputational damage ongoing.",
  },
];

const VENDORS = [
  {
    name: "Core Banking Platform",
    prob: 0.34,
    records: 85_000_000,
    days: 14,
    desc: "Processes transactions for 47 institutions. RSA-encrypted APIs.",
  },
  {
    name: "Payment Processing Network",
    prob: 0.41,
    records: 450_000_000,
    days: 3,
    desc: "Handles $2.3T annual volume. TLS/RSA encrypted endpoints.",
  },
  {
    name: "Cloud Infrastructure",
    prob: 0.28,
    records: 120_000_000,
    days: 7,
    desc: "Hosts trading systems and customer data. RSA certificate chain.",
  },
  {
    name: "Regulatory Reporting Vendor",
    prob: 0.22,
    records: 22_000_000,
    days: 21,
    desc: "Files SEC, FINRA, CFTC reports. Sensitive financial data access.",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: "100vh",
    background: C.bg,
    color: C.white,
    fontFamily: "'Poppins', 'Segoe UI', sans-serif",
    padding: "0",
  },
  header: {
    background: C.surface,
    borderBottom: `2px solid ${C.orange}`,
    padding: "20px 40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  logo: { fontSize: 22, fontWeight: 900, color: C.orange, letterSpacing: 4 },
  tagline: { fontSize: 11, color: C.dim, marginTop: 4 },
  main: { maxWidth: 1100, margin: "0 auto", padding: "40px 24px" },
  card: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    padding: 28,
    marginBottom: 24,
  },
  label: {
    fontSize: 11,
    color: C.orange,
    fontWeight: 700,
    letterSpacing: 2,
    marginBottom: 6,
    display: "block",
    textTransform: "uppercase",
  },
  input: {
    width: "100%",
    background: "#0d0d0d",
    border: `1px solid ${C.border}`,
    borderRadius: 4,
    color: C.white,
    padding: "10px 14px",
    fontSize: 13,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
  },
  btn: {
    background: C.orange,
    color: "#000",
    border: "none",
    borderRadius: 4,
    padding: "12px 28px",
    fontWeight: 900,
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  btnSm: {
    background: "transparent",
    color: C.orange,
    border: `1px solid ${C.orange}`,
    borderRadius: 4,
    padding: "8px 18px",
    fontWeight: 700,
    fontSize: 12,
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: 1,
  },
  h1: {
    fontSize: 28,
    fontWeight: 900,
    color: C.orange,
    marginBottom: 8,
    letterSpacing: 2,
  },
  h2: {
    fontSize: 18,
    fontWeight: 700,
    color: C.orange,
    marginBottom: 16,
    letterSpacing: 1,
  },
  h3: {
    fontSize: 13,
    fontWeight: 700,
    color: C.orange,
    marginBottom: 10,
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  divider: {
    border: "none",
    borderTop: `1px solid ${C.border}`,
    margin: "20px 0",
  },
  danger: { color: C.red, fontWeight: 700 },
  success: { color: C.green, fontWeight: 700 },
  warning: { color: C.yellow },
  dim: { color: C.dim, fontSize: 12 },
  tab: (active) => ({
    padding: "10px 20px",
    cursor: "pointer",
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: "uppercase",
    border: "none",
    borderBottom: active ? `2px solid ${C.orange}` : "2px solid transparent",
    background: "transparent",
    color: active ? C.orange : C.dim,
    transition: "all 0.2s",
  }),
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
  grid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 },
  metricCard: (color) => ({
    background: C.surfaceHigh,
    border: `1px solid ${color}22`,
    borderRadius: 6,
    padding: "16px 20px",
    textAlign: "center",
  }),
  metricVal: (color) => ({
    fontSize: 28,
    fontWeight: 900,
    color,
    letterSpacing: 1,
  }),
  metricLbl: {
    fontSize: 10,
    color: C.dim,
    letterSpacing: 2,
    textTransform: "uppercase",
    marginTop: 4,
  },
  bar: (pct, color) => ({
    width: `${pct}%`,
    height: 8,
    background: color,
    borderRadius: 4,
    transition: "width 0.8s ease",
  }),
  barBg: {
    width: "100%",
    height: 8,
    background: C.surfaceHigh,
    borderRadius: 4,
    overflow: "hidden",
  },
  terminal: {
    background: "#050505",
    border: `1px solid ${C.border}`,
    borderRadius: 4,
    padding: 16,
    fontFamily: "monospace",
    fontSize: 12,
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: `1px solid ${C.border}`,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// CONTACT FORM
// ─────────────────────────────────────────────────────────────────────────────
function ContactForm({ onSuccess }) {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    company: "",
    address: "",
    email: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const writeToSheet = async (data) => {
    const res = await fetch(CONFIG.GOOGLE_SCRIPT_URL, {
      method: "POST",
      body: JSON.stringify({
        action: "submit",
        firstName: data.firstName,
        lastName: data.lastName,
        company: data.company,
        address: data.address,
        email: data.email,
        phone: data.phone,
        token: data.token,
      }),
    });
    const json = await res.json();
    if (json.status === "error") throw new Error(json.message);
    // If duplicate email, reuse existing token from sheet
    return json.status === "duplicate"
      ? { token: json.token }
      : { token: data.token };
  };

  const sendVerificationEmail = async (data) => {
    const verifyUrl = `${CONFIG.APP_URL}?token=${data.token}&email=${encodeURIComponent(data.email)}`;
    const payload = {
      service_id: CONFIG.EMAILJS_SERVICE_ID,
      template_id: CONFIG.EMAILJS_TEMPLATE_ID,
      user_id: CONFIG.EMAILJS_PUBLIC_KEY,
      template_params: {
        to_email: data.email,
        to_name: `${data.firstName} ${data.lastName}`,
        company: data.company,
        verify_url: verifyUrl,
        from_name: "Qubit Guard",
      },
    };
    const res = await fetch("https://api.emailjs.com/api/v1.0/email/send", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to send verification email");
  };

  const handleSubmit = async () => {
    if (
      !form.firstName ||
      !form.lastName ||
      !form.company ||
      !form.email ||
      !form.phone
    ) {
      setError("Please fill in all required fields.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const token = generateToken();
      const data = { ...form, token };
      await writeToSheet(data);
      await sendVerificationEmail(data);
      onSuccess(form.email);
    } catch (e) {
      setError(
        "Something went wrong. Please try again or contact support@qubitguardian.com",
      );
    }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 600, margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: 40 }}>
        <div style={S.h1}>QUANTUM RISK INTELLIGENCE SUITE</div>
        <div
          style={{ color: C.dim, fontSize: 13, marginTop: 8, lineHeight: 1.6 }}
        >
          Powered by Origin Quantum / QPanda Live Simulation
          <br />
          Enter your details to access the free assessment tool.
        </div>
      </div>

      <div style={S.card}>
        <div style={S.h2}>Access Request</div>
        <div style={{ ...S.dim, marginBottom: 20 }}>
          All fields marked * are required. Your information is stored securely
          and used only to provide access to this tool.
        </div>

        <div style={S.grid2}>
          <div>
            <label style={S.label}>First Name *</label>
            <input
              style={S.input}
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              placeholder="John"
            />
          </div>
          <div>
            <label style={S.label}>Last Name *</label>
            <input
              style={S.input}
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              placeholder="Doe"
            />
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={S.label}>Company Name *</label>
          <input
            style={S.input}
            name="company"
            value={form.company}
            onChange={handleChange}
            placeholder="MyCompany"
          />
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={S.label}>Business Address</label>
          <input
            style={S.input}
            name="address"
            value={form.address}
            onChange={handleChange}
            placeholder="123 Main St, Austin, TX 78701"
          />
        </div>

        <div style={S.grid2}>
          <div style={{ marginTop: 16 }}>
            <label style={S.label}>Business Email *</label>
            <input
              style={S.input}
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="you@company.com"
              type="email"
            />
          </div>
          <div style={{ marginTop: 16 }}>
            <label style={S.label}>Phone Number *</label>
            <input
              style={S.input}
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="+1 (888) 888-8888"
            />
          </div>
        </div>

        {error && (
          <div
            style={{
              marginTop: 16,
              color: C.red,
              fontSize: 12,
              background: "#2a0a0a",
              padding: "10px 14px",
              borderRadius: 4,
              border: `1px solid ${C.red}33`,
            }}
          >
            {error}
          </div>
        )}

        <button
          style={{
            ...S.btn,
            marginTop: 24,
            width: "100%",
            padding: "14px",
            fontSize: 14,
            opacity: loading ? 0.6 : 1,
          }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "SUBMITTING..." : "REQUEST ACCESS & VERIFY EMAIL"}
        </button>

        <div
          style={{ ...S.dim, marginTop: 16, textAlign: "center", fontSize: 11 }}
        >
          We will send a verification link to your email. Click it to unlock the
          tool instantly.
        </div>
      </div>

      <div style={{ textAlign: "center", marginTop: 32 }}>
        <div style={{ color: C.dim, fontSize: 11 }}>
          Already verified?{" "}
          <span
            style={{ color: C.orange, cursor: "pointer" }}
            onClick={() => {
              const token = prompt(
                "Enter your verification token from the email:",
              );
              const email = prompt("Enter your email address:");
              if (token && email) {
                localStorage.setItem("qg_token", token);
                localStorage.setItem("qg_email", email);
                window.location.reload();
              }
            }}
          >
            Enter token manually
          </span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EMAIL SENT SCREEN
// ─────────────────────────────────────────────────────────────────────────────
function EmailSent({ email }) {
  return (
    <div style={{ maxWidth: 500, margin: "80px auto", textAlign: "center" }}>
      <div style={{ fontSize: 48, marginBottom: 24 }}>📧</div>
      <div style={S.h1}>CHECK YOUR EMAIL</div>
      <div
        style={{ color: C.dim, fontSize: 13, marginTop: 12, lineHeight: 1.8 }}
      >
        We sent a verification link to
        <br />
        <span style={{ color: C.orange, fontWeight: 700 }}>{email}</span>
        <br />
        <br />
        Click the link in the email to unlock the
        <br />
        Quantum Risk Intelligence Suite instantly.
      </div>
      <div style={{ ...S.card, marginTop: 32, textAlign: "left" }}>
        <div style={S.h3}>Didn't receive it?</div>
        <div style={{ ...S.dim, lineHeight: 1.8 }}>
          • Check your spam or junk folder
          <br />
          • Allow 2-3 minutes for delivery
          <br />• Contact{" "}
          <span style={{ color: C.orange }}>support@qubitguardian.com</span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTOR SELECTION
// ─────────────────────────────────────────────────────────────────────────────
const SECTORS = [
  {
    id: "banking",
    label: "Banking & Financial Services",
    icon: "🏦",
    available: true,
    desc: "NSM-10, CNSA 2.0, GLBA, FedRAMP, SEC cyber rule — complete financial sector analysis",
  },
  {
    id: "insurance",
    label: "Insurance",
    icon: "🛡️",
    available: false,
    desc: "HIPAA, NY DFS 23 NYCRR 500, NAIC model law — coming soon",
  },
  {
    id: "healthcare",
    label: "Healthcare & Life Sciences",
    icon: "🏥",
    available: false,
    desc: "HIPAA/HITECH, OCR enforcement, FDA cyber requirements — coming soon",
  },
  {
    id: "defense",
    label: "Defense & Aerospace",
    icon: "⚡",
    available: false,
    desc: "CMMC 2.0, ITAR, NSM-10, CNSA 2.0 — coming soon",
  },
  {
    id: "federal",
    label: "Federal Government",
    icon: "🏛️",
    available: false,
    desc: "FedRAMP, FISMA, NSM-10, CNSA 2.0 — coming soon",
  },
  {
    id: "energy",
    label: "Energy & Utilities",
    icon: "⚡",
    available: false,
    desc: "NERC CIP, TSA pipeline directives, DOE cyber requirements — coming soon",
  },
];

function SectorSelection({ onSelect }) {
  return (
    <div style={{ maxWidth: 700, margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: 40 }}>
        <div style={S.h1}>SELECT YOUR INDUSTRY SECTOR</div>
        <div
          style={{ color: C.dim, fontSize: 13, marginTop: 8, lineHeight: 1.6 }}
        >
          Choose your sector to run a tailored quantum risk assessment.
          <br />
          More sectors launching as we expand our coverage.
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {SECTORS.map((sector) => (
          <div
            key={sector.id}
            onClick={() => sector.available && onSelect(sector.id)}
            style={{
              background: sector.available ? C.surfaceHigh : C.surface,
              border: `1px solid ${sector.available ? C.orange : C.border}`,
              borderRadius: 6,
              padding: "18px 24px",
              cursor: sector.available ? "pointer" : "not-allowed",
              opacity: sector.available ? 1 : 0.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              transition: "all 0.2s",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <span style={{ fontSize: 28 }}>{sector.icon}</span>
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: sector.available ? C.orange : C.dim,
                  }}
                >
                  {sector.label}
                </div>
                <div style={{ fontSize: 11, color: C.dim, marginTop: 3 }}>
                  {sector.desc}
                </div>
              </div>
            </div>
            <div style={{ minWidth: 100, textAlign: "right" }}>
              {sector.available ? (
                <span
                  style={{
                    background: "#0a2a0a",
                    color: C.green,
                    border: `1px solid ${C.green}44`,
                    padding: "4px 12px",
                    borderRadius: 3,
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  AVAILABLE →
                </span>
              ) : (
                <span
                  style={{
                    background: C.surface,
                    color: C.dim,
                    border: `1px solid ${C.border}`,
                    padding: "4px 12px",
                    borderRadius: 3,
                    fontSize: 11,
                  }}
                >
                  COMING SOON
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          textAlign: "center",
          marginTop: 32,
          color: C.dim,
          fontSize: 11,
        }}
      >
        Want your sector prioritized?{" "}
        <a
          href="mailto:srini@qubitguardian.com?subject=Sector Request"
          style={{ color: C.orange }}
        >
          Contact us
        </a>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ORG PROFILE FORM
// ─────────────────────────────────────────────────────────────────────────────
function OrgProfile({ onAnalyze }) {
  const [org, setOrg] = useState({
    name: "",
    revenue: 45e9,
    records: 10e6,
    certs: 3000,
    fedRev: 200e6,
    years: 7,
  });

  const revTiers = [
    { label: "Under $1B", val: 500e6 },
    { label: "$1B – $5B", val: 2.5e9 },
    { label: "$5B – $20B", val: 12e9 },
    { label: "$20B – $100B", val: 45e9 },
    { label: "Over $100B", val: 120e9 },
  ];
  const recTiers = [
    { label: "Under 1M", val: 500e3 },
    { label: "1M – 5M", val: 2.5e6 },
    { label: "5M – 20M", val: 10e6 },
    { label: "20M – 100M", val: 50e6 },
    { label: "Over 100M", val: 150e6 },
  ];
  const certTiers = [
    { label: "Under 500", val: 250 },
    { label: "500 – 1,500", val: 900 },
    { label: "1,500 – 5,000", val: 3000 },
    { label: "5,000 – 15,000", val: 9000 },
    { label: "Over 15,000", val: 20000 },
  ];
  const fedTiers = [
    { label: "None", val: 0 },
    { label: "Under $50M", val: 25e6 },
    { label: "$50M – $500M", val: 200e6 },
    { label: "$500M – $2B", val: 1e9 },
    { label: "Over $2B", val: 3e9 },
  ];

  const TierSelect = ({ label, tiers, field }) => (
    <div>
      <label style={S.label}>{label}</label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {tiers.map((t) => (
          <button
            key={t.val}
            style={{
              ...S.btnSm,
              background: org[field] === t.val ? C.orange : "transparent",
              color: org[field] === t.val ? "#000" : C.orange,
              fontSize: 11,
            }}
            onClick={() => setOrg((o) => ({ ...o, [field]: t.val }))}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div style={S.card}>
      <div style={S.h2}>⚡ Configure Your Institution Profile</div>
      <div style={{ ...S.dim, marginBottom: 24 }}>
        Enter your actual figures for a precise risk calculation. All data is
        processed locally in your browser.
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={S.label}>Institution Name</label>
        <input
          style={{ ...S.input, maxWidth: 400 }}
          value={org.name}
          onChange={(e) => setOrg((o) => ({ ...o, name: e.target.value }))}
          placeholder="e.g. Wells Fargo"
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <TierSelect label="Annual Revenue" tiers={revTiers} field="revenue" />
        <TierSelect label="Customer Records" tiers={recTiers} field="records" />
        <TierSelect
          label="RSA / ECC Key Inventory"
          tiers={certTiers}
          field="certs"
        />
        <TierSelect
          label="Federal Contract Revenue"
          tiers={fedTiers}
          field="fedRev"
        />

        <div>
          <label style={S.label}>HNDL Risk Horizon</label>
          <div style={{ display: "flex", gap: 8 }}>
            {[
              { label: "5 Years (Optimistic)", val: 5 },
              { label: "7 Years (Consensus)", val: 7 },
              { label: "10 Years (Conservative)", val: 10 },
            ].map((t) => (
              <button
                key={t.val}
                style={{
                  ...S.btnSm,
                  background: org.years === t.val ? C.orange : "transparent",
                  color: org.years === t.val ? "#000" : C.orange,
                  fontSize: 11,
                }}
                onClick={() => setOrg((o) => ({ ...o, years: t.val }))}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <button
        style={{ ...S.btn, marginTop: 28, padding: "13px 40px" }}
        onClick={() =>
          onAnalyze({ ...org, name: org.name || "Your Institution" })
        }
      >
        RUN QUANTUM RISK ANALYSIS →
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// QUANTUM CIRCUIT DISPLAY
// ─────────────────────────────────────────────────────────────────────────────
function QuantumCircuit({ result, factor, hndlProb, years }) {
  return (
    <div style={S.terminal}>
      <div style={{ color: C.yellow, marginBottom: 8 }}>
        ▶ Running Grover's Algorithm — searching RSA key space...
      </div>
      <div style={{ color: C.orange }}>
        Quantum circuit result: <span style={{ color: C.white }}>{result}</span>
      </div>
      <div style={{ color: C.white }}>
        Quantum vulnerability factor:{" "}
        <span style={{ color: C.yellow }}>{factor.toFixed(3)}</span>
      </div>
      <div style={{ color: C.red, fontWeight: 700 }}>
        HNDL attack probability over {years} years:{" "}
        {(hndlProb * 100).toFixed(1)}%
      </div>
      <div style={{ color: C.dim, marginTop: 8, fontSize: 11 }}>
        Powered by Origin Quantum / QPanda — FIPS 203 compliant simulation
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCENARIO COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
function RiskRow({ label, value, color, sub }) {
  return (
    <div
      style={{
        ...S.row,
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 2,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          alignItems: "center",
        }}
      >
        <span style={{ fontSize: 13 }}>{label}</span>
        <span
          style={{
            color,
            fontWeight: 700,
            fontSize: 14,
            fontFamily: "Poppins",
          }}
        >
          {value}
        </span>
      </div>
      {sub && <div style={{ ...S.dim, fontSize: 11 }}>{sub}</div>}
    </div>
  );
}

function ScenarioFederal({ org, qData }) {
  const fedRev = org.fedRev;
  const certs = org.certs;
  const revenue = org.revenue;
  const suspension = fedRev * 0.3;
  const debarment = fedRev * 2.5;
  const fca = Math.floor(certs / 10) * 27894;
  const reputational = revenue * 0.04;
  const migration = certs * 2500;
  const total = suspension + debarment + fca + reputational;
  const roi = total / migration;

  return (
    <div>
      <QuantumCircuit {...qData} />
      <div style={{ ...S.grid3, marginTop: 24 }}>
        <div style={S.metricCard(C.red)}>
          <div style={S.metricVal(C.red)}>{fmt(total)}</div>
          <div style={S.metricLbl}>Total Federal Exposure</div>
        </div>
        <div style={S.metricCard(C.green)}>
          <div style={S.metricVal(C.green)}>{fmt(migration)}</div>
          <div style={S.metricLbl}>PQC Migration Cost</div>
        </div>
        <div style={S.metricCard(C.orange)}>
          <div style={S.metricVal(C.orange)}>{roi.toFixed(0)}x</div>
          <div style={S.metricLbl}>Return on Migration</div>
        </div>
      </div>
      <div style={{ ...S.card, marginTop: 20 }}>
        <div style={S.h3}>Risk Breakdown</div>
        <RiskRow
          label="Contract suspension (30% of federal revenue)"
          value={fmt(suspension)}
          color={C.red}
          sub={`30% of ${fmt(fedRev)} federal revenue`}
        />
        <RiskRow
          label="Debarment risk (2.5x annual federal revenue)"
          value={fmt(debarment)}
          color={C.red}
          sub="Loss of all federal contract eligibility"
        />
        <RiskRow
          label={`False Claims Act (${Math.floor(certs / 10)} violations @ $27,894)`}
          value={fmt(fca)}
          color={C.yellow}
        />
        <RiskRow
          label="Reputational loss (4% of total revenue)"
          value={fmt(reputational)}
          color={C.yellow}
        />
        <hr style={S.divider} />
        <RiskRow
          label="NET RISK AVOIDED BY MIGRATING NOW"
          value={fmt(total - migration)}
          color={C.green}
        />
      </div>
      <div style={{ ...S.card, marginTop: 16 }}>
        <div style={S.h3}>CNSA 2.0 Compliance Deadlines</div>
        {[
          {
            year: "2025",
            desc: "Software/firmware signing must use CNSA 2.0",
            urgency: "PAST DUE",
            color: C.red,
          },
          {
            year: "2026",
            desc: "CMMC 2.0 Level 2 required for all DoD contractors",
            urgency: "IMMINENT",
            color: C.red,
          },
          {
            year: "2027",
            desc: "Network encryption must use CNSA 2.0",
            urgency: "URGENT",
            color: C.yellow,
          },
          {
            year: "2030",
            desc: "All software systems must use CNSA 2.0",
            urgency: "CRITICAL",
            color: C.yellow,
          },
          {
            year: "2033",
            desc: "Legacy systems final deadline",
            urgency: "PLAN NOW",
            color: C.dim,
          },
        ].map((d) => (
          <div
            key={d.year}
            style={{
              display: "flex",
              gap: 16,
              padding: "8px 0",
              borderBottom: `1px solid ${C.border}`,
              alignItems: "center",
            }}
          >
            <span style={{ color: d.color, fontWeight: 700, minWidth: 40 }}>
              {d.year}
            </span>
            <span
              style={{
                background: `${d.color}22`,
                color: d.color,
                padding: "2px 8px",
                borderRadius: 3,
                fontSize: 10,
                fontWeight: 700,
                minWidth: 80,
                textAlign: "center",
              }}
            >
              {d.urgency}
            </span>
            <span style={{ fontSize: 12, color: C.white }}>{d.desc}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ScenarioBreach({ org, qData }) {
  const records = org.records;
  const revenue = org.revenue;
  const certs = org.certs;
  const migration = certs * 2500;
  const secFine = Math.min(30_000_000, revenue * 0.001);
  const glbaFine = 100_000 * 60;
  const classAction = 6_080_000 * 8.5;
  const reputational = revenue * 0.04;
  const sizeFactor = 1 + (records / 10_000_000) * 0.15 + (revenue / 1e9) * 0.02;
  const grandTotal =
    secFine +
    glbaFine +
    classAction +
    reputational +
    42_000_000 * sizeFactor * 0.35;
  const maxCost = 42_000_000 * sizeFactor;

  return (
    <div>
      <div style={{ ...S.terminal, marginBottom: 20 }}>
        <div style={{ color: C.red, fontWeight: 700 }}>
          ⚠ HNDL ATTACK CONFIRMED
        </div>
        <div style={{ color: C.white }}>
          {org.records.toLocaleString()} customer records decrypted by adversary
        </div>
        <div style={{ color: C.yellow }}>
          Collected over the past {org.years} years — now readable
        </div>
        <div style={{ color: C.dim, marginTop: 8 }}>
          HNDL Probability: {(qData.hndlProb * 100).toFixed(1)}% | Quantum
          factor: {qData.factor.toFixed(3)}
        </div>
      </div>

      <div style={S.card}>
        <div style={S.h3}>Breach Cost Timeline</div>
        {TIMELINE.map((t) => {
          const scaled = t.cost * sizeFactor;
          const pct = Math.min(100, (scaled / maxCost) * 100);
          const isLate = t.day >= 168;
          return (
            <div key={t.day} style={{ marginBottom: 14 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 4,
                }}
              >
                <span
                  style={{
                    color: isLate ? C.red : C.yellow,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  Day {t.day}
                </span>
                <span
                  style={{
                    color: isLate ? C.red : C.white,
                    fontWeight: 700,
                    fontFamily: "Poppins",
                  }}
                >
                  {fmt(scaled)}
                </span>
              </div>
              <div style={S.barBg}>
                <div style={S.bar(pct, isLate ? C.red : C.yellow)} />
              </div>
              <div style={{ fontSize: 11, color: C.dim, marginTop: 3 }}>
                {t.event}
              </div>
              <div style={{ fontSize: 11, color: C.yellow, marginTop: 1 }}>
                → {t.consequence}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ ...S.grid3, marginTop: 20 }}>
        <div style={S.metricCard(C.red)}>
          <div style={S.metricVal(C.red)}>{fmt(grandTotal)}</div>
          <div style={S.metricLbl}>Total Breach Cost</div>
        </div>
        <div style={S.metricCard(C.green)}>
          <div style={S.metricVal(C.green)}>{fmt(migration)}</div>
          <div style={S.metricLbl}>PQC Migration Cost</div>
        </div>
        <div style={S.metricCard(C.red)}>
          <div style={S.metricVal(C.red)}>{fmt(grandTotal - migration)}</div>
          <div style={S.metricLbl}>Cost of Inaction</div>
        </div>
      </div>
    </div>
  );
}

function ScenarioBenchmark({ org, qData }) {
  const migration = org.certs * 2500;
  const laggard = org.revenue * 0.015;
  const attrition = org.revenue * 0.008;
  const talent = 2_500_000;
  const audit = 1_800_000;
  const totalLag = laggard + attrition + talent + audit;

  return (
    <div>
      <QuantumCircuit {...qData} />
      <div style={{ ...S.card, marginTop: 20 }}>
        <div style={S.h3}>Industry PQC Migration Progress (2025)</div>
        {[
          ...PEER_BANKS,
          { name: org.name, pct: 0, status: "Not Started", yours: true },
        ].map((b) => (
          <div key={b.name} style={{ marginBottom: 12 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  color: b.yours
                    ? C.red
                    : b.benchmark
                      ? C.yellow
                      : b.pct >= 50
                        ? C.green
                        : C.white,
                  fontWeight: b.yours || b.benchmark ? 700 : 400,
                }}
              >
                {b.name} {b.yours ? "← YOU" : ""}{" "}
                {b.benchmark ? "← BENCHMARK" : ""}
              </span>
              <span
                style={{
                  fontFamily: "Poppins",
                  fontSize: 12,
                  color: b.yours ? C.red : b.pct >= 50 ? C.green : C.yellow,
                }}
              >
                {b.pct}%
              </span>
            </div>
            <div style={S.barBg}>
              <div
                style={S.bar(
                  b.pct,
                  b.yours ? C.red : b.pct >= 50 ? C.green : C.yellow,
                )}
              />
            </div>
          </div>
        ))}
      </div>

      <div style={{ ...S.grid2, marginTop: 16 }}>
        <div style={S.metricCard(C.red)}>
          <div style={S.metricVal(C.red)}>{fmt(totalLag)}</div>
          <div style={S.metricLbl}>Annual Cost of Lagging</div>
        </div>
        <div style={S.metricCard(C.green)}>
          <div style={S.metricVal(C.green)}>{fmt(migration)}</div>
          <div style={S.metricLbl}>One-Time Migration Cost</div>
        </div>
      </div>

      <div style={{ ...S.card, marginTop: 16 }}>
        <div style={S.h3}>Competitive Timeline</div>
        {[
          {
            year: "2025",
            event: "JPMorgan and Goldman complete PQC migration.",
            impact: "Regulators begin asking all others for PQC roadmaps.",
            color: C.green,
          },
          {
            year: "2026",
            event: "CMMC 2.0 and NSM-10 enforcement begins.",
            impact: "Early movers capture quantum-safe certification premium.",
            color: C.yellow,
          },
          {
            year: "2027",
            event: "SEC requires PQC disclosure in cybersecurity risk filings.",
            impact: "Laggards face investor scrutiny and proxy challenges.",
            color: C.yellow,
          },
          {
            year: "2028",
            event: "First major HNDL breach publicly confirmed.",
            impact: "Non-migrated institutions face class action surge.",
            color: C.red,
          },
        ].map((e) => (
          <div
            key={e.year}
            style={{ padding: "10px 0", borderBottom: `1px solid ${C.border}` }}
          >
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <span style={{ color: e.color, fontWeight: 700, minWidth: 40 }}>
                {e.year}
              </span>
              <div>
                <div style={{ fontSize: 12, color: C.white }}>{e.event}</div>
                <div style={{ fontSize: 11, color: C.dim, marginTop: 3 }}>
                  {e.impact}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ScenarioSupplyChain({ org, qData }) {
  const migration = org.certs * 2500;
  let totalVendor = 0;
  const vendorRows = VENDORS.map((v) => {
    const exp = Math.min(
      v.prob * v.records * 210 * (org.records / 50_000_000),
      org.revenue * 0.08,
    );
    totalVendor += exp;
    return { ...v, exp };
  });
  const counterparty = totalVendor * 3.2;
  const regulatory = totalVendor * 0.35;
  const total = totalVendor + counterparty + regulatory;

  return (
    <div>
      <QuantumCircuit {...qData} />
      <div style={{ ...S.card, marginTop: 20 }}>
        <div style={S.h3}>Vendor Quantum Attack Surface</div>
        {vendorRows.map((v) => (
          <div
            key={v.name}
            style={{ padding: "12px 0", borderBottom: `1px solid ${C.border}` }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ color: C.red, fontWeight: 700, fontSize: 13 }}>
                {v.name}
              </span>
              <span
                style={{ color: C.red, fontWeight: 700, fontFamily: "Poppins" }}
              >
                {fmt(v.exp)}
              </span>
            </div>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>
              {v.desc}
            </div>
            <div style={{ fontSize: 11, color: C.yellow, marginTop: 2 }}>
              Breach probability: {(v.prob * 100).toFixed(0)}% | Cascade:{" "}
              {v.days} days | Records: {(v.records / 1e6).toFixed(0)}M
            </div>
          </div>
        ))}
      </div>

      <div style={{ ...S.grid3, marginTop: 16 }}>
        <div style={S.metricCard(C.yellow)}>
          <div style={S.metricVal(C.yellow)}>{fmt(totalVendor)}</div>
          <div style={S.metricLbl}>Direct Vendor Exposure</div>
        </div>
        <div style={S.metricCard(C.red)}>
          <div style={S.metricVal(C.red)}>{fmt(counterparty)}</div>
          <div style={S.metricLbl}>Counterparty Cascade (3.2x)</div>
        </div>
        <div style={S.metricCard(C.red)}>
          <div style={S.metricVal(C.red)}>{fmt(total)}</div>
          <div style={S.metricLbl}>Total Supply Chain Exposure</div>
        </div>
      </div>

      <div style={{ ...S.card, marginTop: 16 }}>
        <div style={S.h3}>Vendor PQC Requirements to Demand Now</div>
        {[
          "PQC Roadmap Attestation — Require written CNSA 2.0 migration plan from all Tier 1 vendors by Q2 2025",
          "Certificate Inventory — Demand full RSA/ECC key inventory from all Tier 1 vendors within 90 days",
          "Contractual PQC Clauses — Amend MSAs to require Kyber-768 and Dilithium by 2027 or face termination",
          "Third-Party Audit Rights — Include PQC compliance in annual vendor security assessments",
        ].map((r, i) => (
          <div
            key={i}
            style={{
              padding: "8px 0",
              borderBottom: `1px solid ${C.border}`,
              fontSize: 12,
              color: C.yellow,
            }}
          >
            {i + 1}. {r}
          </div>
        ))}
        <div
          style={{
            marginTop: 16,
            padding: "12px 16px",
            background: "#0a2a0a",
            border: `1px solid ${C.green}33`,
            borderRadius: 4,
          }}
        >
          <div style={{ color: C.green, fontWeight: 700, fontSize: 12 }}>
            Migrating your own stack for {fmt(migration)} signals to vendors you
            are serious about PQC requirements.
          </div>
        </div>
      </div>
    </div>
  );
}

function ScenarioBoard({ org, qData }) {
  const revenue = org.revenue;
  const records = org.records;
  const certs = org.certs;
  const fedRev = org.fedRev;
  const migration = certs * 2500;
  const hndlProb = qData.hndlProb;
  const breachExp = records * 210 * hndlProb;
  const fedExp = fedRev * 2.5;
  const vendorExp = breachExp * 0.6;
  const reputational = revenue * 0.04;
  const total = breachExp + fedExp + vendorExp + reputational;
  const posture =
    hndlProb > 0.7 ? "CRITICAL" : hndlProb > 0.45 ? "ELEVATED" : "MODERATE";
  const postureColor =
    hndlProb > 0.7 ? C.red : hndlProb > 0.45 ? C.yellow : C.green;
  const roi = total / migration;

  return (
    <div>
      <div style={{ ...S.card, border: `2px solid ${postureColor}` }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                color: C.dim,
                letterSpacing: 2,
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              QUANTUM RISK BOARD SUMMARY
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, color: C.orange }}>
              {org.name.toUpperCase()}
            </div>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>
              Prepared by Qubit Guard | qubitguardian.com
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div
              style={{
                fontSize: 11,
                color: C.dim,
                letterSpacing: 2,
                textTransform: "uppercase",
              }}
            >
              Overall Posture
            </div>
            <div style={{ fontSize: 32, fontWeight: 900, color: postureColor }}>
              {posture}
            </div>
            <div style={{ fontSize: 12, color: postureColor }}>
              HNDL Probability: {(hndlProb * 100).toFixed(1)}% over {org.years}{" "}
              years
            </div>
          </div>
        </div>
      </div>

      <div style={{ ...S.grid2, marginTop: 16 }}>
        {[
          {
            label: "Total Quantum Risk Exposure",
            val: fmt(total),
            color: C.red,
          },
          {
            label: "PQC Migration Investment",
            val: fmt(migration),
            color: C.green,
          },
          {
            label: "Quantum Risk as % of Revenue",
            val: `${((total / revenue) * 100).toFixed(1)}%`,
            color: C.red,
          },
          {
            label: "Return on Migration",
            val: `${roi.toFixed(0)}x`,
            color: C.green,
          },
        ].map((m) => (
          <div key={m.label} style={S.metricCard(m.color)}>
            <div style={S.metricVal(m.color)}>{m.val}</div>
            <div style={S.metricLbl}>{m.label}</div>
          </div>
        ))}
      </div>

      <div style={{ ...S.card, marginTop: 16 }}>
        <div style={S.h3}>Risk Category Breakdown</div>
        {[
          {
            label: "Customer Data (HNDL breach)",
            val: breachExp,
            status: hndlProb > 0.5 ? "RED" : "YELLOW",
            color: hndlProb > 0.5 ? C.red : C.yellow,
          },
          {
            label: "Federal Contract Revenue",
            val: fedExp,
            status: fedRev > 100e6 ? "RED" : "YELLOW",
            color: fedRev > 100e6 ? C.red : C.yellow,
          },
          {
            label: "Third-Party / Supply Chain",
            val: vendorExp,
            status: "YELLOW",
            color: C.yellow,
          },
          {
            label: "Reputational / Market Cap Impact",
            val: reputational,
            status: "YELLOW",
            color: C.yellow,
          },
        ].map((r) => (
          <div key={r.label} style={{ ...S.row }}>
            <span style={{ fontSize: 13 }}>{r.label}</span>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <span
                style={{
                  color: r.color,
                  fontWeight: 700,
                  fontFamily: "Poppins",
                }}
              >
                {fmt(r.val)}
              </span>
              <span
                style={{
                  background: `${r.color}22`,
                  color: r.color,
                  padding: "2px 8px",
                  borderRadius: 3,
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                {r.status}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ ...S.card, marginTop: 16 }}>
        <div style={S.h3}>Key Board Questions</div>
        {[
          {
            q: "Has management completed a quantum cryptography risk assessment?",
            status: "NOT INITIATED",
            color: C.red,
          },
          {
            q: "What is our PQC migration roadmap and timeline?",
            status: "NO ROADMAP — PEER AVG: 38%",
            color: C.red,
          },
          {
            q: "Are we compliant with NSM-10 and CNSA 2.0 mandates?",
            status: "NON-COMPLIANT",
            color: C.red,
          },
          {
            q: "What is our vendor quantum risk posture?",
            status: "UNASSESSED",
            color: C.yellow,
          },
        ].map((q, i) => (
          <div
            key={i}
            style={{ padding: "12px 0", borderBottom: `1px solid ${C.border}` }}
          >
            <div style={{ fontSize: 12, color: C.white }}>
              Q{i + 1}: {q.q}
            </div>
            <div
              style={{
                color: q.color,
                fontWeight: 700,
                fontSize: 11,
                marginTop: 4,
              }}
            >
              STATUS: {q.status}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          ...S.card,
          marginTop: 16,
          border: `1px solid ${C.green}44`,
          background: "#0a1a0a",
        }}
      >
        <div
          style={{
            color: C.green,
            fontWeight: 900,
            fontSize: 14,
            marginBottom: 12,
          }}
        >
          ✓ RECOMMENDED BOARD RESOLUTION
        </div>
        <div style={{ color: C.green, fontSize: 13, lineHeight: 1.8 }}>
          Authorize {fmt(migration)} PQC migration program.
          <br />
          Expected risk reduction: {fmt(total)}
          <br />
          Implementation timeline: 18-24 months with Qubit Guard.
          <br />
          <br />
          <span style={{ color: C.orange }}>
            Contact Qubit Guard to begin: qubitguardian.com
          </span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PDF EXPORT
// ─────────────────────────────────────────────────────────────────────────────
function generatePDF(org, qData, scenarios = "all", activeTab = null) {
  const title =
    scenarios === "all"
      ? `Qubit Guard — Full Executive Report — ${org.name}`
      : `Qubit Guard — ${activeTab} Report — ${org.name}`;

  const date = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const hndlPct = (qData.hndlProb * 100).toFixed(1);
  const migration = org.certs * 2500;
  const fedExp = org.fedRev * 2.5;
  const breachExp = org.records * 210 * qData.hndlProb;
  const vendorExp = breachExp * 0.6;
  const reputational = org.revenue * 0.04;
  const total = breachExp + fedExp + vendorExp + reputational;
  const posture =
    qData.hndlProb > 0.7
      ? "CRITICAL"
      : qData.hndlProb > 0.45
        ? "ELEVATED"
        : "MODERATE";
  const roi = (total / migration).toFixed(0);

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #111; background: #fff; }
    .header { background: #1B3A6B; color: white; padding: 24px 40px; }
    .logo { font-size: 24px; font-weight: 900; color: #F5A623; letter-spacing: 3px; }
    .tagline { font-size: 11px; color: #aaa; margin-top: 4px; }
    .org-name { font-size: 18px; font-weight: 700; margin-top: 8px; }
    .date { font-size: 11px; color: #aaa; margin-top: 2px; }
    .content { padding: 32px 40px; }
    .section { margin-bottom: 28px; }
    .section-title { font-size: 14px; font-weight: 900; color: #1B3A6B; text-transform: uppercase;
      letter-spacing: 1px; border-bottom: 2px solid #1B3A6B; padding-bottom: 6px; margin-bottom: 14px; }
    .posture-box { background: ${qData.hndlProb > 0.7 ? "#fff0f0" : "#fff8e0"}; 
      border: 2px solid ${qData.hndlProb > 0.7 ? "#E53935" : "#FFC107"};
      border-radius: 6px; padding: 16px 20px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center; }
    .posture-label { font-size: 11px; color: #FFF; text-transform: uppercase; letter-spacing: 1px; }
    .posture-value { font-size: 28px; font-weight: 900; color: ${qData.hndlProb > 0.7 ? "#E53935" : "#FFC107"}; }
    .metrics { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 12px; margin-bottom: 20px; }
    .metric { background: #f8f9fa; border: 1px solid #e0e0e0; border-radius: 4px; padding: 12px; text-align: center; }
    .metric-val { font-size: 20px; font-weight: 900; }
    .metric-lbl { font-size: 9px; color: #FFF; text-transform: uppercase; letter-spacing: 1px; margin-top: 3px; }
    .red { color: #E53935; } .green { color: #43A047; } .yellow { color: #F9A825; } .orange { color: #F5A623; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 16px; font-size: 11px; }
    th { background: #1B3A6B; color: white; padding: 8px 10px; text-align: left; font-size: 10px; text-transform: uppercase; }
    td { padding: 7px 10px; border-bottom: 1px solid #eee; }
    tr:nth-child(even) td { background: #f8f9fa; }
    .resolution { background: #e8f5e9; border: 1px solid #43A04733; border-radius: 4px; padding: 16px 20px; }
    .resolution-title { font-weight: 900; color: #2E7D32; margin-bottom: 8px; }
    .footer { background: #f5f5f5; border-top: 1px solid #ddd; padding: 16px 40px; 
      display: flex; justify-content: space-between; font-size: 10px; color: #888; }
    .qc-label { background: #FFF3E0; border: 1px solid #F5A62333; padding: 6px 12px; 
      border-radius: 3px; font-size: 10px; color: #E65100; font-weight: 700; }
    @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
  </style>
</head>
<body>
  <div class="header">
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
  <img 
    src="https://qubitguardian.com/wp-content/uploads/2024/06/Qubit-Guard-new-logo.png" 
    alt="Qubit Guard Logo" 
    style={{ height: "50px" }} 
  />
  <div>
    <div style={S.tagline}>Post-Quantum Cryptography Migration Specialists  |  assess.qubitguardian.com  |  Austin, Texas</div>
  </div>
</div>
    
    <div class="org-name">${org.name.toUpperCase()} — QUANTUM RISK ASSESSMENT</div>
    <div class="date">Generated: ${date}  |  HNDL Horizon: ${org.years} years  |  Quantum Circuit: Origin Quantum / QPanda</div>
  </div>

  <div class="content">

    <div class="posture-box">
      <div>
        <div class="posture-label">Overall Quantum Risk Posture</div>
        <div class="posture-value">${posture}</div>
        <div style="font-size:11px;color:#FFF;margin-top:4px">HNDL Probability: ${hndlPct}% over ${org.years} years  |  Migration Status: 0% complete</div>
      </div>
      <div style="text-align:right">
        <div class="posture-label">Quantum Circuit Result</div>
        <div style="font-size:13px;font-weight:700;color:#1B3A6B;margin-top:4px">${qData.result}</div>
        <div style="font-size:11px;color:#FFF">Factor: ${qData.factor.toFixed(3)}</div>
      </div>
    </div>

    <div class="metrics">
      <div class="metric"><div class="metric-val red">${fmt(total)}</div><div class="metric-lbl">Total Risk Exposure</div></div>
      <div class="metric"><div class="metric-val green">${fmt(migration)}</div><div class="metric-lbl">Migration Cost</div></div>
      <div class="metric"><div class="metric-val red">${((total / org.revenue) * 100).toFixed(1)}%</div><div class="metric-lbl">Risk as % Revenue</div></div>
      <div class="metric"><div class="metric-val green">${roi}x</div><div class="metric-lbl">Return on Migration</div></div>
    </div>

    <div class="section">
      <div class="section-title">Scenario 1 — Federal Contract Revenue at Risk</div>
      <table>
        <tr><th>Risk Item</th><th>Exposure</th></tr>
        <tr><td>Contract suspension (30% of federal revenue)</td><td class="red" style="font-weight:700">${fmt(org.fedRev * 0.3)}</td></tr>
        <tr><td>Debarment risk (2.5x annual federal revenue)</td><td class="red" style="font-weight:700">${fmt(org.fedRev * 2.5)}</td></tr>
        <tr><td>False Claims Act violations</td><td style="color:#F9A825;font-weight:700">${fmt(Math.floor(org.certs / 10) * 27894)}</td></tr>
        <tr><td>Reputational loss (4% of revenue)</td><td style="color:#F9A825;font-weight:700">${fmt(reputational)}</td></tr>
        <tr><td style="font-weight:900">TOTAL FEDERAL EXPOSURE</td><td class="red" style="font-weight:900">${fmt(org.fedRev * 0.3 + org.fedRev * 2.5 + Math.floor(org.certs / 10) * 27894 + reputational)}</td></tr>
        <tr><td style="font-weight:900;color:#2E7D32">PQC MIGRATION COST</td><td class="green" style="font-weight:900">${fmt(migration)}</td></tr>
      </table>
    </div>

    <div class="section">
      <div class="section-title">Scenario 2 — Breach Timeline Summary</div>
      <table>
        <tr><th>Cost Category</th><th>Amount</th></tr>
        <tr><td>Incident response and forensics</td><td>${fmt(42000000 * 0.12)}</td></tr>
        <tr><td>Customer notification and credit monitoring</td><td>${fmt(42000000 * 0.08)}</td></tr>
        <tr><td>SEC fine (material cybersecurity incident)</td><td class="red">${fmt(Math.min(30000000, org.revenue * 0.001))}</td></tr>
        <tr><td>GLBA daily fines (60 days @ $100K)</td><td class="red">$6.0M</td></tr>
        <tr><td>Class action settlement</td><td class="red">${fmt(6080000 * 8.5)}</td></tr>
        <tr><td>Reputational loss</td><td class="red">${fmt(reputational)}</td></tr>
        <tr><td style="font-weight:900">TOTAL BREACH COST</td><td class="red" style="font-weight:900">${fmt(breachExp + 42000000 * 0.35)}</td></tr>
      </table>
      <div style="font-size:11px;color:#FFF">Average FS breach containment: 168 days (IBM Cost of Data Breach 2025)</div>
    </div>

    <div class="section">
      <div class="section-title">Scenario 3 — Competitor Benchmarking</div>
      <table>
        <tr><th>Institution</th><th>PQC Progress</th><th>Status</th></tr>
        <tr><td>JPMorgan Chase</td><td>78%</td><td style="color:#43A047;font-weight:700">Advanced</td></tr>
        <tr><td>Goldman Sachs</td><td>71%</td><td style="color:#43A047;font-weight:700">Advanced</td></tr>
        <tr><td>Bank of America</td><td>54%</td><td style="color:#F9A825">In Progress</td></tr>
        <tr><td>Wells Fargo</td><td>48%</td><td style="color:#F9A825">In Progress</td></tr>
        <tr><td>Industry Average</td><td>38%</td><td style="color:#F9A825">Planning</td></tr>
        <tr><td style="font-weight:900;color:#E53935">${org.name}</td><td class="red" style="font-weight:900">0%</td><td class="red" style="font-weight:900">Not Started</td></tr>
      </table>
    </div>

    <div class="section">
      <div class="section-title">Scenario 4 — Supply Chain Exposure</div>
      <table>
        <tr><th>Vendor</th><th>Breach Probability</th><th>Your Exposure</th></tr>
        <tr><td>Core Banking Platform</td><td>34%</td><td class="red">${fmt(org.revenue * 0.05)}</td></tr>
        <tr><td>Payment Processing Network</td><td>41%</td><td class="red">${fmt(org.revenue * 0.08)}</td></tr>
        <tr><td>Cloud Infrastructure</td><td>28%</td><td class="red">${fmt(org.revenue * 0.06)}</td></tr>
        <tr><td>Regulatory Reporting Vendor</td><td>22%</td><td style="color:#F9A825">${fmt(org.revenue * 0.01)}</td></tr>
        <tr><td style="font-weight:900">TOTAL SUPPLY CHAIN EXPOSURE</td><td></td><td class="red" style="font-weight:900">${fmt(vendorExp * 3.2)}</td></tr>
      </table>
    </div>

    <div class="section">
      <div class="section-title">Scenario 5 — Board Dashboard</div>
      <table>
        <tr><th>Risk Category</th><th>Exposure</th><th>Status</th></tr>
        <tr><td>Customer Data (HNDL breach)</td><td class="red">${fmt(breachExp)}</td><td class="red" style="font-weight:700">RED</td></tr>
        <tr><td>Federal Contract Revenue</td><td class="red">${fmt(fedExp)}</td><td class="red" style="font-weight:700">RED</td></tr>
        <tr><td>Third-Party / Supply Chain</td><td style="color:#F9A825">${fmt(vendorExp)}</td><td style="color:#F9A825;font-weight:700">YELLOW</td></tr>
        <tr><td>Reputational / Market Cap</td><td style="color:#F9A825">${fmt(reputational)}</td><td style="color:#F9A825;font-weight:700">YELLOW</td></tr>
        <tr><td style="font-weight:900">TOTAL QUANTUM RISK EXPOSURE</td><td class="red" style="font-weight:900">${fmt(total)}</td><td class="red" style="font-weight:900">RED</td></tr>
        <tr><td style="font-weight:900;color:#2E7D32">PQC MIGRATION INVESTMENT</td><td class="green" style="font-weight:900">${fmt(migration)}</td><td class="green" style="font-weight:900">ACT NOW</td></tr>
      </table>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-top:12px">
        <div class="metric"><div class="metric-val red">${((total / org.revenue) * 100).toFixed(1)}%</div><div class="metric-lbl">Quantum Risk / Revenue</div></div>
        <div class="metric"><div class="metric-val green">${((migration / org.revenue) * 100).toFixed(2)}%</div><div class="metric-lbl">Migration Cost / Revenue</div></div>
        <div class="metric"><div class="metric-val green">${roi}x</div><div class="metric-lbl">Return on Migration</div></div>
      </div>
    </div>

    <div class="resolution">
      <div class="resolution-title">✓ RECOMMENDED BOARD RESOLUTION</div>
      <div style="color:#1B5E20;font-size:12px;line-height:1.8">
        Authorize ${fmt(migration)} PQC migration program.<br>
        Expected risk reduction: ${fmt(total)}<br>
        Implementation timeline: 18-24 months with Qubit Guard.<br>
        <strong>Contact Qubit Guard: assess.qubitguardian.com  |  Austin, Texas</strong>
      </div>
    </div>

  </div>

  <div class="footer">
    <div>© 2026 Qubit Guard  |  assess.qubitguardian.com  |  Austin, Texas  |  Post-Quantum Cryptography Migration Specialists</div>
    <div>All figures based on IBM Cost of Data Breach 2025, OCR enforcement data, NSM-10, and CNSA 2.0 mandates.</div>
  </div>
</body>
</html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `QubitGuard_${org.name.replace(/\s+/g, "_")}_Risk_Report_${new Date().toISOString().slice(0, 10)}.html`;
  a.click();
  URL.revokeObjectURL(url);
}

function PDFButton({ org, qData, tab, full = false }) {
  return (
    <button
      onClick={() => generatePDF(org, qData, full ? "all" : "single", tab)}
      style={{
        background: full ? C.orange : "transparent",
        color: full ? "#000" : C.orange,
        border: `1px solid ${C.orange}`,
        borderRadius: 4,
        padding: full ? "10px 20px" : "7px 14px",
        fontWeight: 700,
        fontSize: 11,
        cursor: "pointer",
        fontFamily: "Poppins",
        letterSpacing: 1,
        textTransform: "uppercase",
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {full
        ? "⬇ Download Full Executive Report (PDF)"
        : "⬇ Download This Report"}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN TOOL
// ─────────────────────────────────────────────────────────────────────────────
const TABS = [
  { id: "federal", label: "Federal Risk" },
  { id: "breach", label: "Breach Timeline" },
  { id: "benchmark", label: "Benchmarking" },
  { id: "supply", label: "Supply Chain" },
  { id: "board", label: "Board Dashboard" },
];

function RiskTool({ userEmail, sector }) {
  const [org, setOrg] = useState(null);
  const [activeTab, setActiveTab] = useState("federal");
  const [qData, setQData] = useState(null);
  const [running, setRunning] = useState(false);

  const handleAnalyze = (orgData) => {
    setRunning(true);
    setTimeout(() => {
      const q = simulateQuantum();
      const hndlProb = calcHndl(q.factor, orgData.years);
      setQData({ ...q, hndlProb, years: orgData.years });
      setOrg(orgData);
      setRunning(false);
    }, 1200);
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ fontSize: 11, color: C.dim, letterSpacing: 2 }}>
              QUANTUM RISK INTELLIGENCE SUITE
            </div>
            {sector && (
              <span
                style={{
                  background: "#0a1a2a",
                  color: C.orange,
                  border: `1px solid ${C.orange}44`,
                  padding: "2px 8px",
                  borderRadius: 3,
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                {sector === "banking" ? "Banking & Financial Services" : sector}
              </span>
            )}
          </div>
          {org && (
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: C.orange,
                marginTop: 4,
              }}
            >
              {org.name}
            </div>
          )}
        </div>
        <div style={{ ...S.dim, fontSize: 11 }}>
          Logged in as <span style={{ color: C.orange }}>{userEmail}</span>
          {org && (
            <button
              style={{ ...S.btnSm, marginLeft: 12, fontSize: 10 }}
              onClick={() => setOrg(null)}
            >
              Reconfigure
            </button>
          )}
        </div>
      </div>

      {!org && !running && <OrgProfile onAnalyze={handleAnalyze} />}

      {running && (
        <div style={{ ...S.card, textAlign: "center", padding: 60 }}>
          <div
            style={{
              color: C.orange,
              fontSize: 16,
              fontWeight: 700,
              marginBottom: 16,
            }}
          >
            ⚡ Running Quantum Risk Analysis...
          </div>
          <div style={{ color: C.dim, fontSize: 12 }}>
            Executing live quantum circuit via Origin Quantum / QPanda
          </div>
          <div
            style={{
              marginTop: 24,
              color: C.yellow,
              fontFamily: "Poppins",
              fontSize: 12,
            }}
          >
            Grover's Algorithm → RSA key space search → HNDL probability
            calculation
          </div>
        </div>
      )}

      {org && qData && (
        <div>
          <div
            style={{
              display: "flex",
              borderBottom: `1px solid ${C.border}`,
              marginBottom: 24,
              overflowX: "auto",
            }}
          >
            {TABS.map((t) => (
              <button
                key={t.id}
                style={S.tab(activeTab === t.id)}
                onClick={() => setActiveTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 16,
            }}
          >
            <PDFButton org={org} qData={qData} tab={activeTab} />
          </div>
          {activeTab === "federal" && (
            <ScenarioFederal org={org} qData={qData} />
          )}
          {activeTab === "breach" && <ScenarioBreach org={org} qData={qData} />}
          {activeTab === "benchmark" && (
            <ScenarioBenchmark org={org} qData={qData} />
          )}
          {activeTab === "supply" && (
            <ScenarioSupplyChain org={org} qData={qData} />
          )}
          {activeTab === "board" && <ScenarioBoard org={org} qData={qData} />}
          <div
            style={{ display: "flex", justifyContent: "center", marginTop: 24 }}
          >
            <PDFButton org={org} qData={qData} tab={activeTab} full={true} />
          </div>

          <div
            style={{
              ...S.card,
              marginTop: 24,
              textAlign: "center",
              background: "#0a1200",
              border: `1px solid ${C.orange}33`,
            }}
          >
            <div style={{ color: C.orange, fontWeight: 700, marginBottom: 8 }}>
              Ready to start your PQC migration?
            </div>
            <div style={{ color: C.dim, fontSize: 12, marginBottom: 16 }}>
              Qubit Guard executes all four steps — Crypto Inventory, Risk
              Prioritization, Hybrid Migration, and Compliance Mapping.
            </div>
            <a
              href="mailto:support@qubitguardian.com?subject=PQC Migration Inquiry"
              style={{
                ...S.btn,
                textDecoration: "none",
                display: "inline-block",
              }}
            >
              CONTACT QUBIT GUARD →
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// APP ROOT
// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("form"); // form | sent | sector | tool
  const [sector, setSector] = useState(null);
  const [userEmail, setUserEmail] = useState("");

  // Check for verification token in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token") || localStorage.getItem("qg_token");
    const email = params.get("email") || localStorage.getItem("qg_email");
    if (token && email) {
      const decodedEmail = decodeURIComponent(email);
      // Mark as verified in Google Sheet
      fetch(CONFIG.GOOGLE_SCRIPT_URL, {
        method: "POST",
        body: JSON.stringify({ action: "verify", token, email: decodedEmail }),
      }).catch(() => {}); // Non-fatal if this fails
      localStorage.setItem("qg_token", token);
      localStorage.setItem("qg_email", decodedEmail);
      setUserEmail(decodedEmail);
      setScreen("tool");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <img
              src="https://qubitguardian.com/wp-content/uploads/2024/06/Qubit-Guard-new-logo.png"
              alt="Qubit Guard Logo"
              style={{ height: "50px" }}
            />
            <div>
              <div style={S.tagline}>Post-Quantum Cryptography Specialists</div>
            </div>
          </div>
        </div>
        {screen === "tool" && (
          <div style={{ display: "flex", gap: 12 }}>
            <a
              href="https://qubitguardian.com"
              style={{ ...S.btnSm, textDecoration: "none", fontSize: 11 }}
            >
              ← Back to Website
            </a>
          </div>
        )}
      </div>

      <div style={S.main}>
        {screen === "form" && (
          <ContactForm
            onSuccess={(email) => {
              setUserEmail(email);
              setScreen("sent");
            }}
          />
        )}
        {screen === "sent" && <EmailSent email={userEmail} />}
        {screen === "sector" && (
          <SectorSelection
            onSelect={(s) => {
              setSector(s);
              setScreen("tool");
            }}
          />
        )}
        {screen === "tool" && (
          <RiskTool userEmail={userEmail} sector={sector} />
        )}
      </div>

      <div
        style={{
          textAlign: "center",
          padding: "24px",
          borderTop: `1px solid ${C.border}`,
          color: C.dim,
          fontSize: 11,
        }}
      >
        © 2025 Qubit Guard | qubitguardian.com | Austin, Texas | Post-Quantum
        Cryptography Migration Specialists
        <br />
        All risk figures based on 2025 IBM Cost of Data Breach Report, OCR
        enforcement data, NSM-10, and CNSA 2.0 mandates.
      </div>
    </div>
  );
}
